import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';

const GRID_STATE_API = '/umbraco/Api/GridState';

/**
 * RTK Query API slice for grid state persistence endpoints.
 * Handles saving / loading / removing per-user grid layout state
 * (column visibility, sorting, column sizing, page size).
 */
export const gridStateApi = createApi({
  reducerPath: 'gridStateApi',
  baseQuery,
  tagTypes: ['GridState'],
  endpoints: (builder) => ({
    /**
     * Save grid state (column visibility, sorting, sizing, page size)
     * Backend sets UserLogin server-side
     */
    saveGridState: builder.mutation<void, { gridId: number; options: string }>({
      query: ({ gridId, options }) => ({
        url: `${GRID_STATE_API}/SaveState`,
        method: 'POST',
        body: { GridId: gridId, Options: options, UserLogin: '' },
      }),
      invalidatesTags: (_result, _error, { gridId }) => [{ type: 'GridState', id: gridId }],
    }),

    /**
     * Get saved grid state for current user
     */
    getGridState: builder.query<string | null, { gridId: number }>({
      query: ({ gridId }) => ({
        url: `${GRID_STATE_API}/GetState`,
        method: 'POST',
        body: { GridId: gridId, UserLogin: '' },
      }),
      keepUnusedDataFor: 15,
      providesTags: (_result, _error, { gridId }) => [{ type: 'GridState', id: gridId }],
      transformResponse: (response: Record<string, unknown>) => {
        // Backend returns { code, data } — data is the Options JSON string (or null)
        return (response?.data ?? response?.Data ?? null) as string | null;
      },
    }),

    /**
     * Remove saved grid state for current user
     */
    removeGridState: builder.mutation<void, { gridId: number }>({
      query: ({ gridId }) => ({
        url: `${GRID_STATE_API}/RemoveState`,
        method: 'POST',
        body: { GridId: gridId, UserLogin: '' },
      }),
      invalidatesTags: (_result, _error, { gridId }) => [{ type: 'GridState', id: gridId }],
    }),
  }),
});

export const { useSaveGridStateMutation, useGetGridStateQuery, useRemoveGridStateMutation } =
  gridStateApi;
